import {assertNotNullable, ObservableValue, urlOverrideQueryParamsForEncodedUrl} from '@wix/devzai-utils-common';
import type {WixEcomCheckoutCatalogLineItem, WixEcomCheckoutCustomLineItem} from '@wix/devzai-common-wix';
import type {DoppeViewerLang} from '../../client-server-common/doppe-viewer/doppe-viewer-lang';
import type {DoppeHtmlSiteInfo} from '../../client-server-common/types/doppe-html-site';
import {
    doppeHtmlSiteResolvePageUrl,
    DoppeHtmlSiteUriMappingType,
} from '../../client-server-common/types/doppe-html-site';
import {
    doppeResolveCheckoutContinueShoppingUrl,
    doppeResolveCheckoutSuccessUrl,
} from '../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import {doppeViewerGetHeadlessToken} from './doppe-viewer-headless-client-service/doppe-viewer-headless-client-service-utils';

/**
 * Client service for managing the eCommerce cart in the viewer.
 * Uses the Wix Headless SDK `currentCart` module — the SDK automatically manages
 * cart identity via the visitor's OAuthStrategy tokens (stored in cookies).
 *
 * Exposes an `observableCartVersion` counter that increments on every mutation,
 * enabling reactive UI updates without storing cart IDs.
 */
export class DoppeCartClientService extends DoppeViewerAppClientServiceBase {

    /**
     * Reactive change signal — increments on every cart mutation (add, update, remove, delete).
     * Subscribe via `useObservableValue` to trigger refetches when the cart changes.
     */
    public readonly observableCartVersion = new ObservableValue<number>(0);

    private getHeadlessSdkOptions() {
        return {
            clientId: this.clientServices.headlessService.assertClientId(),
            tokens: doppeViewerGetHeadlessToken(this.clientServices),
        };
    }

    private bumpVersion() {
        this.observableCartVersion.setValue(this.observableCartVersion.getValue() + 1);
    }

    /**
     * Add a catalog line item to the current visitor's cart.
     * The SDK auto-creates a cart if none exists.
     */
    public async addToCart(
        lineItem: DoppeCartClientService.CatalogLineItemInput
    ): Promise<void> {
        const {
            doppeViewerHeadlessSdkCartAddToCurrentCart,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        await doppeViewerHeadlessSdkCartAddToCurrentCart({
            ...this.getHeadlessSdkOptions(),
            lineItems: [lineItem],
        });

        this.bumpVersion();
    }

    /** Retrieves the current visitor's cart. Returns null if no cart exists. */
    public async getCart() {
        const {
            doppeViewerHeadlessSdkCartGetCurrentCart,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        return await doppeViewerHeadlessSdkCartGetCurrentCart(this.getHeadlessSdkOptions());
    }

    /** Updates the quantity of a line item in the current visitor's cart. */
    public async updateLineItemQuantity(lineItemId: string, quantity: number) {
        const {
            doppeViewerHeadlessSdkCartUpdateCurrentCartLineItemQuantity,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        const result = await doppeViewerHeadlessSdkCartUpdateCurrentCartLineItemQuantity({
            ...this.getHeadlessSdkOptions(),
            lineItems: [{id: lineItemId, quantity: quantity}],
        });

        this.bumpVersion();
        return result;
    }

    /** Removes line items from the current visitor's cart by their IDs. */
    public async removeLineItems(lineItemIds: string[]) {
        const {
            doppeViewerHeadlessSdkCartRemoveLineItemsFromCurrentCart,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        const result = await doppeViewerHeadlessSdkCartRemoveLineItemsFromCurrentCart({
            ...this.getHeadlessSdkOptions(),
            lineItemIds: lineItemIds,
        });

        this.bumpVersion();
        return result;
    }

    /**
     * Creates a checkout from the current visitor's cart and returns the checkout page URL
     * with successUrl and continueShoppingUrl query params already applied.
     * Sets a checkout-pending flag so the cart fallback can be cleared on successful return.
     */
    public async createCartCheckoutUrl(options: {
        lang: DoppeViewerLang;
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
    }): Promise<string> {
        const {
            doppeViewerHeadlessSdkCartCreateCheckoutFromCurrentCart,
            doppeViewerHeadlessSdkCartSetCheckoutPending,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        const headlessSdkOptions = this.getHeadlessSdkOptions();

        const result = await doppeViewerHeadlessSdkCartCreateCheckoutFromCurrentCart(headlessSdkOptions);

        doppeViewerHeadlessSdkCartSetCheckoutPending(headlessSdkOptions.clientId);

        const checkoutPageUrl = doppeCartClientServiceBuildCheckoutUrl({
            checkoutId: result.checkoutId,
            lang: options.lang,
            htmlSiteInfo: options.htmlSiteInfo,
        });

        const successUrl = doppeResolveCheckoutSuccessUrl(checkoutPageUrl);
        const continueShoppingUrl = doppeResolveCheckoutContinueShoppingUrl(this.clientServices);

        return urlOverrideQueryParamsForEncodedUrl(checkoutPageUrl, {
            successUrl: successUrl,
            continueShoppingUrl: continueShoppingUrl,
        });
    }

    /** Deletes the current visitor's cart. */
    public async clearCart(): Promise<void> {
        const {
            doppeViewerHeadlessSdkCartDeleteCurrentCart,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        await doppeViewerHeadlessSdkCartDeleteCurrentCart(this.getHeadlessSdkOptions());
        this.bumpVersion();
    }

    /** Applies a coupon code to the current visitor's cart. */
    public async applyCoupon(couponCode: string) {
        const {
            doppeViewerHeadlessSdkCartApplyCoupon,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        const result = await doppeViewerHeadlessSdkCartApplyCoupon({
            ...this.getHeadlessSdkOptions(),
            couponCode: couponCode,
        });

        this.bumpVersion();
        return result;
    }

    /** Removes the applied coupon from the current visitor's cart. */
    public async removeCoupon() {
        const {
            doppeViewerHeadlessSdkCartRemoveCoupon,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        const result = await doppeViewerHeadlessSdkCartRemoveCoupon(this.getHeadlessSdkOptions());

        this.bumpVersion();
        return result;
    }

    /** Updates the buyer note on the current visitor's cart. */
    public async updateBuyerNote(note: string) {
        const {
            doppeViewerHeadlessSdkCartUpdateBuyerNote,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        const result = await doppeViewerHeadlessSdkCartUpdateBuyerNote({
            ...this.getHeadlessSdkOptions(),
            buyerNote: note,
        });

        this.bumpVersion();
        return result;
    }

    /** Estimates totals for the current visitor's cart. */
    public async estimateTotals() {
        const {
            doppeViewerHeadlessSdkCartEstimateTotals,
        } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

        return await doppeViewerHeadlessSdkCartEstimateTotals(this.getHeadlessSdkOptions());
    }

    /**
     * Clears the cart fallback data if a cart checkout just completed successfully.
     * Must be called synchronously when the page detects a checkout success redirect
     * (iframeModalURL present) — before the URL is cleaned and before any async cart
     * operations run. Uses direct localStorage access to avoid the async dynamic import.
     */
    public handleCheckoutRedirect(): void {
        const headlessClientId = this.clientServices.headlessService.getClientIdOrNull();

        if (!headlessClientId) {
            return;
        }

        const pendingKey = `doppe-cart-checkout-pending:${headlessClientId}`;

        try {
            if (localStorage.getItem(pendingKey) !== null) {
                localStorage.removeItem(`doppe-cart-fallback-items:${headlessClientId}`);
                localStorage.removeItem(pendingKey);
            }
        } catch {
            // localStorage not available
        }
    }

    /**
     * Creates a checkout from line items and returns the checkout page URL.
     * Uses the headless SDK for catalog-only checkouts when headless is available,
     * falling back to the server API for custom line items or when headless is not configured.
     */
    public async createCheckoutUrl(
        params: DoppeCartClientService.CreateCheckoutParams
    ): Promise<DoppeCartClientService.CreateCheckoutResult> {
        const headlessClientId = this.clientServices.headlessService.getClientIdOrNull();
        const hasCustomLineItems = params.customLineItems.length > 0;

        if (headlessClientId && !hasCustomLineItems) {
            const {
                doppeViewerHeadlessSdkCreateCheckout,
            } = await import('../doppe-viewer-headless-sdk/doppe-viewer-headless-sdk-cart.dynamic');

            const result = await doppeViewerHeadlessSdkCreateCheckout({
                clientId: headlessClientId,
                tokens: doppeViewerGetHeadlessToken(this.clientServices),
                catalogLineItems: params.catalogLineItems,
                buyerNote: params.buyerNote,
            });

            return {
                checkoutPageUrl: doppeCartClientServiceBuildCheckoutUrl({
                    checkoutId: result.checkoutId,
                    lang: params.lang,
                    htmlSiteInfo: params.htmlSiteInfo,
                }),
            };
        }

        return await this.clientServices.getApiClient().createWixEcomCheckout(params);
    }
}

export namespace DoppeCartClientService {
    /** Input for catalog line items, matching the headless SDK facade interface. */
    export interface CatalogLineItemInput {
        appId: string;
        catalogItemId: string;
        quantity: number;
        catalogOptions?: Record<string, any>;
    }

    export interface CreateCheckoutParams {
        customLineItems: WixEcomCheckoutCustomLineItem[];
        catalogLineItems: WixEcomCheckoutCatalogLineItem[];
        lang: DoppeViewerLang;
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        buyerNote?: string;
    }

    export interface CreateCheckoutResult {
        checkoutPageUrl: string;
    }
}

function doppeCartClientServiceBuildCheckoutUrl(
    options: {
        checkoutId: string;
        lang: DoppeViewerLang;
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
    }
): string {
    return assertNotNullable(
        doppeHtmlSiteResolvePageUrl({
            doppeHtmlSiteInfo: assertNotNullable(options.htmlSiteInfo),
            htmlSiteUriMappingType: DoppeHtmlSiteUriMappingType.Ecom,
            pagePath: '/checkout?checkoutId=' + encodeURIComponent(options.checkoutId),
            viewerLang: options.lang,
        })
    );
}
